import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  resetPassword,
  changeEmail,
} from "@microdoing/shared/services/login/actions";
import {
  RoundInput,
  MainTitle,
  Box,
  MainText,
  MainButton,
  PressableOpacity,
} from "../../components/common";
import i18n from "@microdoing/shared/i18n";
import sanitizeName from "../../utils/sanitizeName";
import { useNavigation } from "@react-navigation/native";
import {
  getLoginEmail,
  getLoginPasswordResetError,
  getLoginRequesting,
  getLoginResetPasswordSent,
} from "@microdoing/shared/services/login/selectors";
import HeaderScreen from "app/components/common/HeaderScreen";
import { Keyboard } from "react-native";
import KeyboardAvoidWrapper from "app/components/common/KeyboardAvoidWrapper";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebForgotPasswordScreen from "./WebForgotPasswordScreen";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const ForgotPasswordScreen = () => {
  const email = useSelector(getLoginEmail);
  const requesting = useSelector(getLoginRequesting);
  const passwordResetError = useSelector(getLoginPasswordResetError);
  const resetPasswordSent = useSelector(getLoginResetPasswordSent);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const insets = useSafeAreaInsets();

  const onChangeEmail = (text: string) => {
    dispatch(changeEmail(text));
  };

  const onSubmit = () => {
    if (email !== "") {
      dispatch(resetPassword({ email: sanitizeName(email) }));
    }
    Keyboard.dismiss();
  };

  const goToLogin = () => {
    navigation.goBack();
  };

  if (isWebDesktop) {
    return (
      <WebForgotPasswordScreen
        onChangeEmail={onChangeEmail}
        onSubmit={onSubmit}
        goToLogin={goToLogin}
      />
    );
  }

  return (
    <Box
      variant="screenView"
      backgroundColor="neutral.white"
      style={{ paddingTop: insets.top }}
    >
      <HeaderScreen title="" leftIcon />
      <KeyboardAvoidWrapper>
        <Box>
          <MainTitle variant="l7-bold">
            {i18n.t("your-account-email")}
          </MainTitle>
          <Box marginTop="m">
            <RoundInput
              keyboardType="email-address"
              onChangeText={onChangeEmail}
              value={email}
              placeholder={i18n.t("your-email")}
              autoFocus
              onSubmitEditing={onSubmit}
            />
          </Box>
          <MainButton
            isLoading={requesting}
            text={i18n.t("send-email")}
            onPress={onSubmit}
            isDisabled={!email || requesting}
            textColor={!email || requesting ? "neutral.700" : "neutral.white"}
          />
          {!!passwordResetError && (
            <MainText
              variant="l4-regular"
              color="system.red.950"
              marginTop="xs"
            >
              {passwordResetError}
            </MainText>
          )}
          {resetPasswordSent && (
            <MainText
              variant="l3-medium"
              textAlign="center"
              marginLeft="sm"
              marginRight="sm"
              marginTop="xs"
            >
              {i18n.t("reset-password-sent")}
            </MainText>
          )}
          <Box alignItems="center">
            <PressableOpacity onPress={goToLogin}>
              <Box flexDirection="row" justifyContent="center">
                <MainText variant="l3-medium" marginRight="xxs">
                  {i18n.t("i-remember-password")}
                </MainText>
                <MainText variant="l3-semibold" color="brand.500">
                  {i18n.t("log-me")}
                </MainText>
              </Box>
            </PressableOpacity>
          </Box>
        </Box>
      </KeyboardAvoidWrapper>
    </Box>
  );
};

export default ForgotPasswordScreen;
